<script lang="ts">
import layout from '~/consumer/layouts/DefaultLayout.vue'
export default { layout }

</script>
<script setup lang="ts">import { computed as _computed, ref as _ref } from 'vue';

import type GiftTemplate from '@consumer/types/GiftTemplate'
import type { GiftlyDesign, MultiplaceGift, MultiplaceGiftsContentVotes } from '@consumer/types/serializers'
import CustomizerWrapper from '@consumer/modules/multiPlaceGiftIdea/CustomizerWrapper.vue'
import MainContent from '@consumer/modules/multiPlaceGiftIdea/MainContent.vue'
import MultiPlaceGiftIdeasApi from '@consumer/api/MultiPlaceGiftIdeasApi'
import PopUpCTA from '@consumer/modules/multiPlaceGiftIdea/PopUpCTA.vue'
import { asyncOpenModal, openMultiplaceCustomizerModal } from '@consumer/services/modals'
import { cloneDeep } from '@shared/object'
import { isDesktop } from '@consumer/utils/breakpoints'
import { useElementBounding, useUrlSearchParams } from '@vueuse/core'
import { useAsyncJob } from '@shared/composables/useAsyncJob'
import { router } from '@shared/inertia'
import { v3WithImageBg } from '@consumer/logic/v3'
import HelpAndDisclosure from '@consumer/modules/v2/layout/HelpAndDisclosure.vue'
import { primaryPlaceInjectionKey } from '@consumer/modules/multiPlaceGiftIdea/composables'
import { faPen } from '@fortawesome/pro-regular-svg-icons'
import { useOrderStore } from '@consumer/services/order'
import { track as trackMetaPixel } from '@consumer/services/metaPixel'
import newCopyLightIcon from '@consumer/assets/icons/new/copy-light.svg?svgicon'
import newGearIcon from '@consumer/assets/icons/new/gear.svg?svgicon'

onMounted(() => useOrderStore().initialize())

withDefaults(defineProps<{
  multiPlaceGiftIdea: MultiplaceGift
  giftlyDesigns: GiftlyDesign[]
  isEditing: boolean
  currentVotes: MultiplaceGiftsContentVotes[]
  canEditMultiplaceGift?: boolean
  productOfferSchema: JSON
}>(), { canEditMultiplaceGift: false, })

onMounted(() => {
  if (gift.value.metaPixelContentId) {
    trackMetaPixel('ViewContent', { content_ids: [gift.value.metaPixelContentId], content_type: 'product' })
  }
})

const breadcrumbs = [
  { name: 'Multi-Place Gifts', href: '/mp', rel: 'nofollow' },
  {
    name: __props.multiPlaceGiftIdea.giftlyMetadata.tagline || '',
    href: __props.multiPlaceGiftIdea.url,
    rel: __props.multiPlaceGiftIdea.indexable ? undefined : 'nofollow',
  },
]

const editUrl = _computed(() => MultiPlaceGiftIdeasApi.draft.path(__props.multiPlaceGiftIdea))

async function openEditDetailsModal () {
  return asyncOpenModal(import('@consumer/modals/multiPlaceGiftIdea/EditDetailsModal.vue'), {
    multiplaceGift: __props.multiPlaceGiftIdea,
  })
}

const urlParams = useUrlSearchParams()

// If the gift was just created and content is being generated async, poll until done
const generatingContent = useAsyncJob(
  async (jid) => {
    const r = await fetch(`/job_status/${jid}`, { headers: { Accept: 'application/json' } })
    if (!r.ok) return { status: 'not_found' }
    return r.json() as Promise<{ status: string; result?: string | null; error?: string | null }>
  },
)

onMounted(() => {
  const jid = urlParams.jid as string | undefined
  if (jid) {
    generatingContent.start(jid)
  }
})

watch(generatingContent.status, (status) => {
  if (status === 'complete' || status === 'failed') {
    router.visit(window.location.pathname, { replace: true })
  }
})

const customDesign = _computed(() => {
  const designName = __props.multiPlaceGiftIdea.giftlyMetadata.designName
  if (!designName || designName === v3WithImageBg) {
    return {
      ...__props.multiPlaceGiftIdea.giftlyMetadata,
      id: __props.multiPlaceGiftIdea.internalSlug,
      imageUrl: __props.multiPlaceGiftIdea.giftlyMetadata?.backgroundImageUrl ?? '',
      provider: 'giftly',
      custom: true,
    } satisfies GiftlyDesign
  }
})

const allDesigns = _computed(() => customDesign.value ? [customDesign.value, ...__props.giftlyDesigns] : __props.giftlyDesigns)

const initialDesignId = urlParams.giftly_design_name || __props.multiPlaceGiftIdea.giftlyMetadata.designName
let selectedDesign = _ref<GiftlyDesign>(findGiftlyDesign(initialDesignId as string))

const gift = _ref<GiftTemplate>({
  amount: urlParams.amount ? parseInt(urlParams.amount.toString()) : __props.multiPlaceGiftIdea.amounts[0],
  places: cloneDeep(__props.multiPlaceGiftIdea.places),
  giftlyMetadata: {
    ...__props.multiPlaceGiftIdea.giftlyMetadata,
    multiPlaceGiftIdeaInternalSlug: __props.multiPlaceGiftIdea.internalSlug,
  },
  metaPixelContentId: __props.multiPlaceGiftIdea.metaPixelContentId,
})

if (!import.meta.env.SSR)
  window.currentGift = gift.value

function findGiftlyDesign (id?: string) {
  return (id && allDesigns.value.find(design => design.id === id)) || allDesigns.value[0]
}

function updateDesign (design: GiftlyDesign) {
  if (gift.value && design) {
    const designName = design.custom ? v3WithImageBg : design.id
    gift.value.giftlyMetadata ||= design
    gift.value.giftlyMetadata.designName = designName
    gift.value.giftlyMetadata.backgroundColor = design.backgroundColor
    gift.value.giftlyMetadata.backgroundImageUrl = design.backgroundImageUrl
    gift.value.giftlyMetadata.maskName = design.maskName
    gift.value.giftlyMetadata.maskColor = design.maskColor
    gift.value.giftlyMetadata.logoColor = design.logoColor
  }
}

function updateAmount (amount: number) {
  gift.value.amount = amount
}

watch(() => __props.multiPlaceGiftIdea.giftlyMetadata.designName, (designName) => {
  selectedDesign.value = findGiftlyDesign(designName)
})

watch(() => selectedDesign.value, () => {
  updateDesign(selectedDesign.value)

  if (!isDesktop.value && !import.meta.env.SSR && !import.meta.env.VITEST) {
    window.scrollTo({ top: 130, behavior: 'smooth' })
  }
}, { immediate: true }) // In case a different design was specified via params.

const primaryGitlyImageRef = ref<HTMLElement>()
const {
  bottom: primaryGiftlyImageBottom,
} = useElementBounding(primaryGitlyImageRef)
const showSecondaryGiftlyImage = computed(() => primaryGiftlyImageBottom.value < 0)

const primaryPlace = computed(() => {
  const specParam = urlParams.spec?.toString()
  const placeSpec = specParam?.match(/-merchantspriority-multiplace-trial-(.*)/)?.[1]
  if (placeSpec) {
    const place = gift.value.places?.find(p => p.id === placeSpec)
    return place
  }
})
provide(primaryPlaceInjectionKey, primaryPlace)

async function openCustomizerIfEditing () {
  if (__props.isEditing) {
    openMultiplaceCustomizerModal({
      gift: gift.value,
      multiplaceGift: __props.multiPlaceGiftIdea,
      createOrEditMultiplace: true,
    })
  }
}
const isEditingField = _ref(false)

const themeColor = _computed(() => selectedDesign.value.themeColor || selectedDesign.value.maskColor)
const logoColor = _computed(() => selectedDesign.value.logoColor)

const canCopyGift = computed(() => __props.canEditMultiplaceGift && !__props.isEditing && __props.multiPlaceGiftIdea.canBeCopied)

async function copyGift () {
  const { newGift } = await MultiPlaceGiftIdeasApi.copy(__props.multiPlaceGiftIdea)
  window.location.href = MultiPlaceGiftIdeasApi.draft.path(newGift)
}
</script>

<template>
  <div style="background-color: #f3f9ff;">
    <div
      v-if="generatingContent.status.value === 'pending'"
      class="flex items-center justify-center gap-2 bg-blue-50 border-b border-blue-200 px-4 py-2 text-sm text-blue-700"
    >
      <span
        class="inline-block w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin flex-shrink-0"
      />
      Generating gift content, this may take a moment…
    </div>
    <div class="relative lg:max-w-screen-lg lg:mx-auto">
      <GBreadcrumbs class="lg:block" :breadcrumbs="breadcrumbs"/>
      <div class="h3 absolute right-5 top-6 flex flex-row gap-3">
        <GIcon
          v-if="canCopyGift"
          class="clickable"
          :svg="newCopyLightIcon"
          size="20px"
          data-testid="copy-button"
          @click="copyGift"
        />
        <a
          v-if="canEditMultiplaceGift && !isEditing"
          :href="editUrl"
          class="clickable"
          rel="nofollow"
          name="Edit"
        >
          <GIcon :svg="newGearIcon" color="black" size="20px"/>
        </a>
      </div>
      <EditButton
        v-if="canEditMultiplaceGift && isEditing"
        class="absolute"
        title="Edit Details"
        data-testid="edit-details-button"
        style="right: 32px; top: 30px;"
        @click="openEditDetailsModal"
      />
    </div>
  </div>
  <main data-qa="MultiPlaceGiftIdea" class="lg:relative">
    <Head>
      <title>{{ multiPlaceGiftIdea.pageTitle }}</title>
      <meta name="description" :content="multiPlaceGiftIdea.description">
      <component :is="'script'" type="application/ld+json">
        {{ productOfferSchema }}
      </component>
    </Head>

    <CustomizerWrapper
      v-if="isDesktop || !isEditing || !isEditingField"
      v-model:selectedDesign="selectedDesign"
      :showSecondaryGiftlyImage="showSecondaryGiftlyImage"
      :themeColor="themeColor"
      :gift="gift"
      :multiPlaceGiftIdea="multiPlaceGiftIdea"
      :giftlyDesigns="allDesigns"
      :isEditing="isEditing"
      :isEditingField="isEditingField"
      @setAmount="updateAmount"
    />

    <div
      class="w-full static rounded-b-30px lg:rounded-none"
      :style="{
        backgroundColor: themeColor,
        transition: 'all 0.2s ease 0s',
      }"
    >
      <div
        class="mx-auto max-w-md lg:max-w-screen-lg lg:h-500px px-8 py-10 lg:p-10 relative pb-7"
      >
        <div class="w-full h-full flex flex-col lg:flex-row gap-5 items-center">
          <PopUpCTA :gift="gift" class="lg:hidden z-1"/>
          <div ref="primaryGitlyImageRef" class="w-full lg:w-470px relative">
            <GiftlyV3Image
              :gift="gift"
              :inCustomizer="true"
              class="w-full"
              @click="openCustomizerIfEditing()"
            />
            <GIcon
              v-if="isEditing"
              :icon="faPen"
              size="lg"
              color="white"
              class="absolute right-0 bottom-0 p-4 clickable"
              :style="{
                filter: `drop-shadow(rgb(0 0 0 / 60%) 0.1em -0.1em 0.75em)` +
                  `drop-shadow(rgb(0 0 0 / 20%) 0 0.1em 0.1em)`
              }"
              @click="openCustomizerIfEditing()"
            />

            <FavoriteButton
              class="!flex !lg:hidden mt-6 mx-auto"
              :favoritable="multiPlaceGiftIdea"
              :themeColor="themeColor"
            />
          </div>
        </div>
      </div>
    </div>

    <MainContent
      :multiPlaceGiftIdea="multiPlaceGiftIdea"
      :gift="gift"
      :themeColor="themeColor"
      :logoColor="logoColor"
      :isEditing="isEditing"
      :currentVotes="currentVotes"
      :canEditMultiplaceGift="canEditMultiplaceGift"
      @startEditingField="isEditingField=true"
      @finishEditingField="isEditingField=false"
    />

  </main>
  <HelpAndDisclosure/>
</template>
